import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Edit, Delete, Search } from '@mui/icons-material';
import {
  getRequest,
  postRequest,
  patchRequest,
  deleteRequest,
  server,
} from '../config';

const ManageTestingUsers = () => {
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [mobileNumber, setMobileNumber] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    const res = await getRequest(`${server.uri}testing`);
    if (res) {
      setUsers(res);
      setAllUsers(res);
    }
  };

  const handleAddUser = () => {
    setMobileNumber('');
    setEditingUser(null);
    setOpenDialog(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setMobileNumber(user.mobileNumber);
    setOpenDialog(true);
  };

  const handleDeleteUser = async (id) => {
    if (window.confirm('Are you sure you want to delete this testing user?')) {
      await deleteRequest(`${server.uri}testing/${id}`);
      fetchUsers();
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleSaveUser = async () => {
    // Validation: must be exactly 10 digits
    if (!/^\d{10}$/.test(mobileNumber)) {
      alert('Mobile number must be exactly 10 digits.');
      return;
    }

    const payload = { mobileNumber };

    if (editingUser) {
      await patchRequest(`${server.uri}testing/${editingUser.id}`, payload);
    } else {
      await postRequest(`${server.uri}testing`, payload);
    }

    fetchUsers();
    handleDialogClose();
  };

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchQuery(value);
    const filtered = allUsers.filter((user) =>
      user.mobileNumber.toLowerCase().includes(value)
    );
    setUsers(filtered);
  };

  return (
    <Container>
      <Typography variant="h4" sx={{ marginTop: 8, marginBottom: 2 }}>
        Manage Testing Users
      </Typography>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <TextField
          variant="outlined"
          placeholder="Search by mobile number"
          value={searchQuery}
          onChange={handleSearch}
          InputProps={{
            startAdornment: <Search sx={{ color: 'gray', marginRight: 1 }} />,
          }}
          sx={{ width: '60%' }}
        />
        <Button variant="contained" onClick={handleAddUser}>
          Add User
        </Button>
      </Box>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: '#f4f6f8' }}>
              <TableRow>
                <TableCell><strong>Mobile Number</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.mobileNumber}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleEditUser(user)}>
                      <Edit />
                    </IconButton>
                    <IconButton color="error" onClick={() => handleDeleteUser(user.testingId)}>
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    No testing users found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Mobile Number"
            value={mobileNumber}
            onChange={(e) => setMobileNumber(e.target.value)}
            inputProps={{ maxLength: 10 }}
            helperText="Enter a valid 10-digit mobile number"
            sx={{ mb: 2, mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleSaveUser} variant="contained">
            {editingUser ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ManageTestingUsers;
